import React from "react";
import { useNavigate } from "react-router-dom";
import Header from "../../components/ui/Header";
import Icon from "../../components/AppIcon";


const NepalFAQ = () => {
  const navigate = useNavigate();

  const faqs = [
    {
      category: "Travel Basics",
      icon: "HelpCircle",
      questions: [
        { q: "Do I need a visa for Nepal?", a: "Most nationalities can get a visa on arrival at Kathmandu airport or land borders. Visa fees start at USD 30 for 15 days. Indian nationals do not require a visa." },
        { q: "What is the best time to visit Nepal?", a: "The best times are spring (March-May) and autumn (September-November) when weather is pleasant and mountain views are clear." },
        { q: "Is Nepal safe for solo travelers?", a: "Yes, Nepal is generally very safe for solo travelers. The locals are friendly and helpful. Standard precautions apply, especially at night." },
        { q: "What language is spoken in Nepal?", a: "Nepali is the official language. English is widely spoken in tourist areas, hotels, and trekking agencies." }
      ]
    },
    {
      category: "Health & Safety",
      icon: "Heart",
      questions: [
        { q: "Do I need vaccinations for Nepal?", a: "Recommended vaccinations include Hepatitis A & B, Typhoid, and Tetanus. COVID-19 vaccination is recommended. Consult your doctor 4-6 weeks before travel." },
        { q: "What is altitude sickness?", a: "Altitude sickness (AMS) occurs when ascending too quickly above 2,500m. Symptoms include headache, nausea, and dizziness. Proper acclimatization is essential." },
        { q: "Is the tap water safe to drink?", a: "No, avoid tap water. Drink bottled or filtered water. Use water purification tablets or a filter when trekking." },
        { q: "What medical facilities are available?", a: "Kathmandu has good hospitals and clinics. Remote areas have basic health posts. Travel insurance with evacuation coverage is strongly recommended." }
      ]
    },
    {
      category: "Money & Costs",
      icon: "DollarSign",
      questions: [
        { q: "What currency is used in Nepal?", a: "The Nepali Rupee (NPR). Check our Travel Information page for the current live exchange rate." },
        { q: "How much does a trek cost?", a: "Costs vary widely. A teahouse trek can cost USD 30-50 per day including guide, porter, accommodation, and meals. Luxury treks cost USD 100-200+ per day." },
        { q: "Are credit cards widely accepted?", a: "Credit cards are accepted at hotels, larger restaurants, and shops in cities. Cash is king in remote areas and smaller establishments." },
        { q: "Should I tip in Nepal?", a: "Tipping is appreciated but not mandatory. For trekking: guide USD 15-20/day, porter USD 8-10/day. Restaurants: 10% for good service." }
      ]
    },
    {
      category: "Trekking & Activities",
      icon: "Mountain",
      questions: [
        { q: "Do I need a guide for trekking?", a: "While not required for all treks, a guide is highly recommended for safety, cultural insight, and supporting local employment. Some restricted areas require a licensed guide." },
        { q: "What permits do I need for trekking?", a: "Most treks require TIMS (Trekkers' Information Management System) card and a national park permit (NPR 3,000-5,000). Restricted areas require special permits." },
        { q: "Can I trek solo in Nepal?", a: "Solo trekking is allowed in most areas, though restricted area treks require a guide. We recommend guides for safety and a richer experience." },
        { q: "What fitness level is required?", a: "Varies by trek. Easy treks (Poon Hill) require basic fitness. Hard treks (Everest Base Camp) require good cardiovascular fitness and prior preparation." }
      ]
    },
    {
      category: "Connectivity",
      icon: "Wifi",
      questions: [
        { q: "Can I use my mobile phone in Nepal?", a: "Yes, buy a local SIM at the airport (Ncell or Nepal Telecom) for affordable data. Your home provider may work on roaming at higher rates." },
        { q: "Is WiFi available in trekking areas?", a: "Yes, most tea houses on popular routes offer WiFi via mobile data hotspots. Connection quality varies; expect basic speeds." },
        { q: "What is the internet speed like in Nepal?", a: "In cities, broadband internet is reliable and decent. In remote areas, mobile data is available but can be slow." }
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main>
        <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-28 bg-cover bg-center" style={{ backgroundImage: "url(https://images.unsplash.com/photo-1578898887932-dce23a595ad4?w=1600&h=600&fit=crop)" }}>
          <div className="absolute inset-0 bg-gradient-to-r from-gray-900/90 via-gray-900/70 to-gray-900/50" />
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl">
              <h1 className="section-heading text-white mb-4">Nepal FAQ</h1>
              <p className="text-lg text-white/80">Frequently asked questions about traveling to Nepal.</p>
            </div>
          </div>
        </section>

        <section className="py-16">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 space-y-10">
            {faqs.map((group, i) => (
              <div key={i}>
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-xl bg-accent/10 flex items-center justify-center">
                    <Icon name={group.icon} size={18} className="text-accent" />
                  </div>
                  <h2 className="font-headline text-xl text-gray-900" style={{ fontWeight: 300 }}>{group.category}</h2>
                </div>
                <div className="space-y-3">
                  {group.questions.map((item, j) => (
                    <details key={j} className="bg-white rounded-xl border border-gray-100 shadow-sm group">
                      <summary className="flex items-center justify-between p-5 cursor-pointer list-none">
                        <span className="font-medium text-gray-900 text-sm pr-4">{item.q}</span>
                        <Icon name="ChevronDown" size={16} className="text-gray-400 group-open:rotate-180 transition-transform flex-shrink-0" />
                      </summary>
                      <div className="px-5 pb-5">
                        <p className="text-sm text-gray-600 leading-relaxed">{item.a}</p>
                      </div>
                    </details>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </section>
      </main>

      <footer className="bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 text-center text-sm text-white/50">
          &copy; {new Date().getFullYear()} Sherpa Trekker. All rights reserved.
        </div>
      </footer>
    </div>
  );
};

export default NepalFAQ;
